/*
  La Sura - Save for later in the basket (drawer and basket page).

  A basket line can be set aside: it leaves the basket and appears under "Saved for later", kept in
  this browser (localStorage, so on this device only), with Move to basket and Remove. A built cake
  is saved with the lines nested under it, from each row's data-lasura-undo (printed for Undo).

  This file draws the list into <lasura-saved-list> (cart-products.liquid), shows the Save buttons
  when storage works, and redraws after each basket change, since Horizon re-renders the basket.
  The actions load on the first tap (assets/lasura-save-later-actions.js), which keeps this small.
*/
import { ThemeEvents } from '@theme/events';

const KEY = 'lasura-saved-for-later-v1';
const MAX = 20;

const storageWorks = (() => {
  try {
    localStorage.setItem(`${KEY}-test`, '1');
    localStorage.removeItem(`${KEY}-test`);
    return true;
  } catch {
    return false;
  }
})();

const read = () => {
  try {
    const list = JSON.parse(localStorage.getItem(KEY) || '[]');
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
};

/** Returns whether the list was stored, so a save never takes a line out of the basket unsaved. */
const write = (list) => {
  try {
    localStorage.setItem(KEY, JSON.stringify(list.slice(0, MAX)));
    return true;
  } catch {
    return false;
  }
};

const words = () => {
  const el = document.querySelector('.lasura-save-text');
  const get = (name) => el?.getAttribute(`data-${name}`) || '';
  return {
    heading: get('heading'),
    note: get('note'),
    move: get('move'),
    remove: get('remove'),
    saved: get('saved'),
    moved: get('moved'),
    error: get('error'),
    saveError: get('save-error'),
    full: get('full'),
  };
};

/** Show the Save buttons (hidden in the markup, so nothing appears without this script or storage). */
const revealButtons = () => {
  if (!storageWorks) return;
  document.querySelectorAll('button[data-lasura-save][hidden]').forEach((button) => button.removeAttribute('hidden'));
};

/* ---------- The saved list ---------- */

/** Where focus goes after the list is redrawn: a saved item's Move button, or the list heading. */
let focusAfter = null;

class LasuraSavedList extends HTMLElement {
  connectedCallback() {
    this.render();
  }

  render() {
    /* A redraw replaces every node, so a control that had focus would drop it on <body>. Note which
       one it was (its class and item) and hand focus to its replacement afterwards. */
    const active = document.activeElement;
    const kept =
      active instanceof HTMLElement && this.contains(active)
        ? {
            className: ['lasura-saved__move', 'lasura-saved__remove', 'lasura-saved__title', 'lasura-saved__heading'].find(
              (name) => active.classList.contains(name)
            ),
            uid: /** @type {HTMLElement | null} */ (active.closest('.lasura-saved__item'))?.dataset.uid || '',
          }
        : null;

    this.#draw();

    if (!kept?.className) return;
    const scope = kept.uid ? this.querySelector(`.lasura-saved__item[data-uid="${CSS.escape(kept.uid)}"]`) : this;
    const target = scope?.querySelector(`.${kept.className}`) || this.querySelector('.lasura-saved__heading');
    if (target instanceof HTMLElement) target.focus({ preventScroll: true });
  }

  #draw() {
    const list = storageWorks ? read() : [];
    const w = words();
    this.replaceChildren();
    if (!list.length || !w.heading) return;

    const inner = document.createElement('section');
    inner.className = 'lasura-saved__inner';
    const headingId = `LasuraSaved-${Math.random().toString(36).slice(2, 8)}`;
    inner.setAttribute('aria-labelledby', headingId);

    const heading = document.createElement('h3');
    heading.className = 'lasura-saved__heading';
    heading.id = headingId;
    heading.tabIndex = -1;
    heading.textContent = w.heading;
    inner.append(heading);

    if (w.note) {
      const note = document.createElement('p');
      note.className = 'lasura-saved__note';
      note.textContent = w.note;
      inner.append(note);
    }

    const ul = document.createElement('ul');
    ul.className = 'lasura-saved__list';
    list.forEach((item) => {
      const li = document.createElement('li');
      li.className = 'lasura-saved__item';
      li.dataset.uid = item.uid;

      const img = document.createElement(item.image ? 'img' : 'span');
      img.className = 'lasura-saved__image';
      if (item.image) {
        img.setAttribute('src', item.image);
        img.setAttribute('alt', '');
        img.setAttribute('loading', 'lazy');
        img.setAttribute('width', '56');
        img.setAttribute('height', '56');
      }
      li.append(img);

      const title = document.createElement(item.url ? 'a' : 'span');
      title.className = 'lasura-saved__title';
      if (item.url) title.setAttribute('href', item.url);
      title.textContent = item.title;
      li.append(title);

      if (item.meta) {
        const meta = document.createElement('span');
        meta.className = 'lasura-saved__meta';
        meta.textContent = item.meta;
        li.append(meta);
      }

      const actions = document.createElement('div');
      actions.className = 'lasura-saved__actions';
      const move = document.createElement('button');
      move.type = 'button';
      move.className = 'lasura-saved__move';
      move.dataset.uid = item.uid;
      move.textContent = w.move;
      move.append(Object.assign(document.createElement('span'), { className: 'visually-hidden', textContent: `: ${item.title}` }));
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'lasura-saved__remove';
      remove.dataset.uid = item.uid;
      remove.textContent = w.remove;
      remove.append(Object.assign(document.createElement('span'), { className: 'visually-hidden', textContent: `: ${item.title}` }));
      actions.append(move, remove);
      li.append(actions);
      ul.append(li);
    });
    inner.append(ul);
    this.append(inner);
  }
}

if (!customElements.get('lasura-saved-list')) customElements.define('lasura-saved-list', LasuraSavedList);

const renderAll = () => {
  document.querySelectorAll('lasura-saved-list').forEach((el) => /** @type {any} */ (el).render?.());
  revealButtons();
  if (!focusAfter) return;
  const { drawer, uid } = focusAfter;
  focusAfter = null;
  const host = Array.from(document.querySelectorAll('lasura-saved-list')).find(
    (el) => Boolean(el.closest('cart-drawer-component')) === drawer
  );
  if (!host) return;
  const target =
    (uid && host.querySelector(`.lasura-saved__move[data-uid="${CSS.escape(uid)}"]`)) ||
    host.querySelector('.lasura-saved__move') ||
    host.querySelector('.lasura-saved__heading') ||
    host.closest('dialog, main')?.querySelector('h1, h2') ||
    document.querySelector('#cart-drawer-heading, main h1, h1');
  if (target instanceof HTMLElement) {
    if (!target.hasAttribute('tabindex') && !/^(A|BUTTON)$/.test(target.tagName)) target.tabIndex = -1;
    target.focus();
  }
};

/* Horizon morphs the basket after each change, which can wipe or replace the list: redraw after. */
document.addEventListener(ThemeEvents.cartUpdate, () => {
  requestAnimationFrame(() => requestAnimationFrame(renderAll));
});
window.addEventListener('storage', (event) => {
  if (event.key === KEY) renderAll();
});

/* ---------- Moving back, or removing from the list ---------- */


/* One listener for every Save, Move and Remove button; the actions load on the first tap. */
let actions = null;
document.addEventListener('click', async (event) => {
  const button = event.target instanceof Element
    ? event.target.closest('button[data-lasura-save], .lasura-saved__move, .lasura-saved__remove')
    : null;
  if (!(button instanceof HTMLButtonElement) || !storageWorks) return;
  const url = document.querySelector('.lasura-save-text')?.getAttribute('data-actions');
  if (!url) return;
  try {
    actions = actions || (await import(url));
    await actions.handle(button, {
      max: MAX,
      read,
      write,
      words,
      renderAll,
      setFocus: (value) => {
        focusAfter = value;
      },
    });
  } catch (error) {
    console.error(error);
  }
});

renderAll();
